import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, CheckCircle2, ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export default function ForgotPassword() {
  const [email, setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent]     = useState(false);
  const { resetPassword }   = useAuth();
  const { toast }           = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({ title: "Enter your email", description: "Please enter your email address to continue.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await resetPassword(email);
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error, variant: "destructive" });
    } else {
      setSent(true);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-8">

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            className="flex justify-center"
          >
            <Link to="/" className="relative flex items-center justify-center group">
              <div className="absolute inset-0 rounded-3xl bg-primary/15 blur-2xl scale-125" />
              <div className="relative rounded-2xl bg-card border border-border/40 shadow-2xl p-3 backdrop-blur-sm group-hover:scale-[1.03] transition-transform">
                <img src="/logo.png" alt="Stratakey" className="h-16 w-auto object-contain rounded-xl" />
              </div>
            </Link>
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.35 }}
            className="text-center space-y-1"
          >
            <h1 className="text-2xl font-bold tracking-tight">Reset your password</h1>
            <p className="text-sm text-muted-foreground">
              {sent ? "Check your inbox for the reset link" : "We'll send a reset link to your email"}
            </p>
          </motion.div>

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22, duration: 0.35 }}
            className="rounded-xl border bg-card shadow-sm p-6"
          >
            {sent ? (
              <div className="flex flex-col items-center gap-3 text-center py-2">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/40">
                  <CheckCircle2 className="h-7 w-7 text-green-600" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold">Reset link sent!</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    We sent a password reset link to <strong>{email}</strong>. Click the link in the email to set a new password.
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground pt-1">
                  Didn't receive it?{" "}
                  <button
                    type="button"
                    className="text-primary font-medium hover:underline"
                    onClick={() => setSent(false)}
                  >
                    Try again
                  </button>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs">Email address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-9 h-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full h-10" disabled={loading || !email.trim()}>
                  {loading
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                    : "Send reset link"
                  }
                </Button>
              </form>
            )}
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.3 }}
            className="text-center pb-4"
          >
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </motion.div>

        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
